import { useEffect, useRef, useState, type RefObject } from 'react';
import { haptic } from './telegram';

/**
 * Жест «утянуть вниз, чтобы закрыть» для нижних листов и модалок.
 *
 * Правила, без которых лист «уплывал за экран»:
 *
 * 1. Состояние — в ref, не в локальных `let` тела компонента: `setDrag`
 *    перерисовывает компонент и обнулял бы их между `move` и `end`, а
 *    `end` тогда выходил раньше `setDrag(0)` и лист оставался сдвинутым.
 * 2. Жест засчитывается только когда содержимое прокручено вверх
 *    (`scrollTop <= 0`) И первое заметное движение пальца — вниз и явно
 *    вертикальнее горизонтали. Пока это не подтвердилось, лист не двигаем:
 *    иначе попытка прокрутить содержимое или качнуть карту тащит окно.
 * 3. Перевод жёстко ограничен сверху (сопротивление после 240 px).
 * 4. `drag` возвращается в 0 не только на `end`/`cancel`, но и на закрытие
 *    слоя (`active === false`). В WebView Telegram нативная прокрутка
 *    иногда перехватывает жест и `touchend`/`touchcancel` не приходят —
 *    без этой страховки `translateY` застревал на `.modal__sheet` и
 *    переживал закрытие: следующее открытие показывало окно уже уехавшим.
 *
 * `exclude` — CSS-селектор области, которая тянется сама (карта).
 */

/** Порог, после которого решаем — это закрытие или прокрутка. */
const ENGAGE_PX = 8;

export function useSwipeDown(
  scrollRef: RefObject<HTMLElement>,
  onClose: () => void,
  opts: { exclude?: string; active?: boolean } = {},
) {
  const active = opts.active ?? true;
  const [drag, setDrag] = useState(0);
  const g = useRef({
    startX: 0,
    startY: 0,
    /** null — ещё не решили; true — тянут вниз закрывать; false — это прокрутка. */
    engaged: null as boolean | null,
  });

  // Слой закрыт (или только что открыт) — жест обнулён, лист на месте.
  useEffect(() => {
    setDrag(0);
    g.current.engaged = false;
  }, [active]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.target as HTMLElement | null;
    if (opts.exclude && t?.closest(opts.exclude)) {
      g.current.engaged = false;
      return;
    }
    const box = scrollRef.current;
    // Не у верха содержимого — это точно прокрутка, жест не наш.
    g.current.engaged = box && box.scrollTop <= 0 ? null : false;
    g.current.startX = e.touches[0].clientX;
    g.current.startY = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (g.current.engaged === false) return;

    const dx = e.touches[0].clientX - g.current.startX;
    const dy = e.touches[0].clientY - g.current.startY;

    if (g.current.engaged === null) {
      // Ждём заметного движения, потом решаем раз и навсегда.
      if (Math.abs(dx) < ENGAGE_PX && Math.abs(dy) < ENGAGE_PX) return;
      // Закрытие — только явный тяг вниз, вертикальнее горизонтали.
      g.current.engaged = dy > 0 && dy > Math.abs(dx);
      if (!g.current.engaged) return;
    }

    // Сопротивление после 240 px: резкий бросок не уводит лист на весь экран.
    setDrag(dy > 240 ? 240 + (dy - 240) * 0.3 : Math.max(0, dy));
  };

  const end = () => {
    const wasEngaged = g.current.engaged === true;
    g.current.engaged = false;
    if (!wasEngaged) return;

    // Порог в треть экрана: случайное движение не закрывает, осознанный
    // бросок — закрывает.
    if (drag > Math.min(160, window.innerHeight / 3)) {
      haptic('light');
      onClose();
    }
    setDrag(0);
  };

  return {
    drag,
    bind: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: end,
      onTouchCancel: end,
    },
  };
}
